import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, HeartPulse, Zap, Leaf } from 'lucide-react';
import { OFFER_URL } from '../data/quizData';
import { fbqTrack } from '../lib/fbq';

const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export const ResultsScreen = ({ firstName, onRedirect }) => {
  const goToOffer = () => {
    fbqTrack('InitiateCheckout', { content_name: 'CoQ10 Ubiquinone', content_category: 'Supplement', value: 50, currency: 'USD' });
    if (onRedirect) onRedirect();
    window.location.href = OFFER_URL;
  };

  return (
    <motion.section
      className="relative min-h-screen w-full bg-brand-bg px-5 sm:px-6 py-8 sm:py-12 overflow-hidden"
      variants={container}
      initial="hidden"
      animate="visible"
      data-testid="results-screen"
    >
      {/* brand bar */}
      <div className="relative max-w-7xl mx-auto px-0 md:px-4 pt-2 pb-5 sm:pb-6 flex items-center justify-between">
        <img src="/brand/young-again-logo.png" alt="Young Again" className="h-9 sm:h-10 md:h-12 w-auto" />
        <span className="hidden sm:inline-block text-xs uppercase tracking-[0.25em] font-semibold text-brand-forest font-heading">
          Feel Young Again. Naturally.
        </span>
      </div>

      <div
        className="pointer-events-none absolute top-0 left-1/3 h-[320px] sm:h-[420px] w-[320px] sm:w-[420px] rounded-full blur-3xl opacity-40"
        style={{ background: 'radial-gradient(circle at center, #D8E5C0 0%, rgba(216,229,192,0) 70%)' }}
      />

      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-16 items-start pt-2 sm:pt-4">
        {/* Left: editorial summary */}
        <div className="lg:col-span-7">
          <motion.div variants={item} className="flex items-center gap-3">
            <div className="h-px w-10 bg-brand-moss" />
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-moss font-heading" data-testid="results-overline">
              Your Personalized Results{firstName ? `, ${firstName}` : ''}
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            className="mt-5 sm:mt-6 font-heading font-bold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-brand-forest leading-[1.1] sm:leading-[1.05]"
            data-testid="results-headline"
          >
            Based on your answers, supporting{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-brand-moss">cellular energy</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-brand-sage -z-0 rounded" aria-hidden />
            </span>{' '}
            may be a smart next step.
          </motion.h1>

          <motion.p variants={item} className="mt-6 text-lg text-brand-muted leading-relaxed font-body max-w-2xl">
            Your responses suggest that energy production, recovery, and healthy aging are priorities right now. <strong className="text-brand-forest font-semibold">CoQ10</strong> is a well-known compound involved in mitochondrial energy production and heart health support — which is why many adults explore it as part of a proactive wellness plan.
          </motion.p>

          <motion.div variants={item} className="mt-8 space-y-4">
            {[
              {
                icon: <Zap className="w-5 h-5 text-brand-forest" />,
                title: 'Supports daily energy',
                body: 'CoQ10 is a natural cofactor inside mitochondria — the cellular engines that power every system in your body.',
              },
              {
                icon: <HeartPulse className="w-5 h-5 text-brand-forest" />,
                title: 'Heart health support',
                body: 'Your heart is one of the most energy-demanding organs — which is why mitochondrial support is often discussed alongside cardiovascular wellness.',
              },
              {
                icon: <Leaf className="w-5 h-5 text-brand-forest" />,
                title: 'Healthy aging',
                body: 'Natural CoQ10 levels may decline with age — a key reason many adults begin exploring supplementation after 40.',
              },
            ].map((b) => (
              <div key={b.title} className="flex gap-4 items-start bg-white border border-brand-border rounded-2xl p-5" data-testid={`result-benefit-${b.title}`}>
                <div className="mt-1 h-10 w-10 rounded-xl bg-brand-sage flex-shrink-0 flex items-center justify-center">
                  {b.icon}
                </div>
                <div>
                  <div className="font-heading font-semibold text-xl text-brand-forest">{b.title}</div>
                  <div className="mt-1 text-sm sm:text-base text-brand-muted font-body leading-relaxed">{b.body}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: product card / CTA */}
        <motion.aside variants={item} className="lg:col-span-5 lg:sticky lg:top-10">
          <div className="relative rounded-3xl overflow-hidden border border-brand-border bg-white shadow-xl shadow-brand-forest/10">
            <div className="relative h-56 sm:h-72 md:h-80 overflow-hidden bg-white">
              <img
                src="/brand/coq10-product.jpg"
                alt="Young Again CoQ10 Ubiquinone — 30 Capsules"
                className="absolute inset-0 w-full h-full object-contain p-4"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(216,229,192,0.35) 0%, rgba(255,255,255,0) 60%)',
                }}
              />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-brand-forest text-white rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold font-heading shadow-lg" data-testid="offer-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-sage" />
                Up to 50% Off
              </div>
              <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.25em] font-semibold text-brand-moss font-heading bg-white/80 backdrop-blur-sm rounded-full px-3 py-1">
                30 Capsules
              </div>
            </div>

            <div className="p-7">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-muted font-heading">Young Again</div>
              <h3 className="mt-2 font-heading font-bold text-3xl text-brand-forest leading-tight">CoQ10 · Ubiquinone</h3>
              <p className="mt-3 text-sm text-brand-muted font-body leading-relaxed">
                A premium CoQ10 formulation designed to support cellular energy production, heart health, and healthy aging — formulated for adults ready for the next chapter.
              </p>

              <ul className="mt-5 space-y-2.5">
                {[
                  'Supports mitochondrial energy production',
                  'Heart & cardiovascular wellness',
                  'Recovery & everyday vitality',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-brand-forest font-body">
                    <span className="mt-0.5 h-5 w-5 rounded-md bg-brand-sage flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-brand-forest" strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={goToOffer}
                className="group mt-7 w-full inline-flex items-center justify-center gap-3 bg-brand-forest text-white hover:bg-brand-forestDark transition-all rounded-xl px-6 py-4 text-sm sm:text-base font-heading font-semibold uppercase tracking-wider shadow-sm hover:shadow-lg"
                data-testid="unlock-offer-btn"
              >
                Unlock Up To 50% Off CoQ10
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>

              <button
                onClick={goToOffer}
                className="mt-3 w-full text-center text-sm text-brand-muted hover:text-brand-forest font-body underline underline-offset-4 transition-colors"
                data-testid="see-offer-link"
              >
                See today's offer →
              </button>

              <div className="mt-5 pt-5 border-t border-brand-border text-xs text-brand-muted font-body leading-relaxed">
                These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
              </div>
            </div>
          </div>
        </motion.aside>
      </div>
    </motion.section>
  );
};
